import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { Spinner } from '@/components/ui/misc'
import { api } from '@/lib/api'
import { cn, formatRelative } from '@/lib/utils'

interface Vote {
  decision: 'approve' | 'reject'
  reason: string | null
  createdAt: string
  reviewerName: string
}

/** Shows the author how far a submission has got, without revealing the queue. */
export function ReviewProgress({ recordId }: { recordId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['review', 'votes', recordId],
    queryFn: () => api.get<{ votes: Vote[]; threshold: number }>(`/review/${recordId}/votes`),
  })

  if (isLoading || !data) return <Spinner />

  const approvals = data.votes.filter((vote) => vote.decision === 'approve').length

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {Array.from({ length: data.threshold }, (_unused, index) => (
          <span
            key={index}
            className={cn(
              'h-1.5 flex-1 rounded-full',
              index < approvals ? 'bg-positive' : 'bg-line',
            )}
          />
        ))}
        <span className="text-[12px] font-semibold text-ink-soft">
          {approvals} of {data.threshold} approvals
        </span>
      </div>

      {data.votes.length === 0 ? (
        <p className="text-[12px] text-ink-muted">
          No reviews yet. Super users and admins see this in their review queue.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {data.votes.map((vote) => (
            <li key={`${vote.reviewerName}-${vote.createdAt}`} className="flex gap-2 text-[12px]">
              <span
                className={cn(
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full',
                  vote.decision === 'approve'
                    ? 'bg-positive-soft text-positive'
                    : 'bg-brand-soft text-brand',
                )}
              >
                {vote.decision === 'approve' ? (
                  <Check className="size-2.5" />
                ) : (
                  <X className="size-2.5" />
                )}
              </span>
              <span className="min-w-0">
                <span className="font-semibold text-ink">{vote.reviewerName}</span>
                <span className="text-ink-muted">
                  {' '}
                  {vote.decision === 'approve' ? 'approved' : 'asked for changes'} ·{' '}
                  {formatRelative(vote.createdAt)}
                </span>
                {vote.reason && <span className="block text-ink-soft">{vote.reason}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
